// Settings → Blocked: every "send straight to Trash" sender filter on each
// inbox you can access, with one-click unblock. Backed by Gmail's own
// filter list, so it includes blocks made in Gmail as well as in Wharf.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Blocked {
  accountId: number;
  accountEmail: string;
  filterId: string;
  from: string;
}
interface BlockedResponse {
  blocked: Blocked[];
  errors: { accountId: number; accountEmail: string; error: string }[];
}

export function BlockedDomainsSettings() {
  const qc = useQueryClient();
  const q = useQuery<BlockedResponse>({
    queryKey: ['blocked-domains'],
    queryFn: async () => {
      const r = await fetch('/api/blocked-domains', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
  const unblock = useMutation({
    mutationFn: async (b: Blocked) => {
      const r = await fetch(`/api/blocked-domains/${b.accountId}/${encodeURIComponent(b.filterId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error === 'needs_reauth' ? 'Reconnect this inbox first (Settings → Accounts)' : 'Could not unblock');
      }
    },
    onSuccess: (_d, b) => {
      toast.success(`Unblocked ${b.from} on ${b.accountEmail}`);
      void qc.invalidateQueries({ queryKey: ['blocked-domains'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not unblock'),
  });

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Checking each inbox's filters…
        </CardContent>
      </Card>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">Couldn't load blocked senders.</CardContent>
      </Card>
    );
  }
  const byAccount = new Map<string, Blocked[]>();
  for (const b of q.data.blocked) {
    const list = byAccount.get(b.accountEmail) ?? [];
    list.push(b);
    byAccount.set(b.accountEmail, list);
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Mail from these senders is moved straight to Trash by a filter on the inbox itself
        (created by "Block" in Wharf, or in Gmail). Unblocking removes the filter; nothing is
        restored automatically — check Trash for anything it already caught.
      </p>
      {q.data.errors.map((e) => (
        <p key={e.accountId} className="text-xs text-amber-600 dark:text-amber-400">
          {e.accountEmail}: {e.error === 'needs_reauth' ? 'reconnect this inbox to see its filters' : 'filters unavailable right now'}
        </p>
      ))}
      {byAccount.size === 0 && q.data.errors.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">No blocked senders.</CardContent>
        </Card>
      )}
      {[...byAccount.entries()].map(([email, list]) => (
        <Card key={email}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Ban className="size-4 text-muted-foreground" strokeWidth={1.75} />
              {email}
              <span className="font-normal text-muted-foreground">· {list.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y p-0">
            {list.map((b) => (
              <div key={b.filterId} className="flex items-center justify-between gap-3 px-6 py-2.5">
                <code className="truncate text-sm">{b.from}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={unblock.isPending}
                  onClick={() => {
                    if (confirm(`Unblock "${b.from}" on ${email}? Future mail from it will land in the inbox again.`)) {
                      unblock.mutate(b);
                    }
                  }}
                >
                  Unblock
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
